import { Coins } from 'lucide-react';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { useAuth, useChatInteract } from '@chainlit/react-client';

import {
  apiClient,
  setAccountExpiredModalActive,
  type UserTokenUsage
} from '@/api';

import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';

import { useFetch } from '@/hooks/useFetch';
import { formatTokenCount, formatTimestamp } from '@/lib/format';
import {
  formatRemaining,
  formatRemainingShort,
  isTempExhausted
} from '@/lib/tempAccount';
import { accountExpiredState } from '@/state/project';

const REFRESH_INTERVAL_MS = 10_000;

function UsageRow({
  label,
  input,
  output,
  total
}: {
  label: string;
  input: number;
  output: number;
  total: number;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="font-mono tabular-nums text-sm font-semibold">
          {formatTokenCount(total)}
        </span>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground font-mono tabular-nums">
        <span>in {formatTokenCount(input)}</span>
        <span>out {formatTokenCount(output)}</span>
      </div>
    </div>
  );
}

export default function TokenUsageIndicator() {
  const { data: auth } = useAuth();
  const { stopTask } = useChatInteract();
  const url = auth?.requireLogin
    ? apiClient.buildEndpoint('/user/token-usage')
    : null;
  const { data, error } = useFetch(url, {
    refreshInterval: REFRESH_INTERVAL_MS
  });
  const usage = data as UserTokenUsage | undefined;

  const [accountExpired, setAccountExpired] = useRecoilState(accountExpiredState);

  useEffect(() => {
    if (accountExpired) return;
    const reason = isTempExhausted(usage?.temp);
    if (reason) {
      // Belt to the backend in-stream cancel: stop any in-flight generation the
      // moment the demo allowance runs out, then lock the UI (#1054).
      stopTask();
      setAccountExpiredModalActive(true);
      setAccountExpired({ reason, logout_in_seconds: 20 });
    }
  }, [usage?.temp, accountExpired, setAccountExpired, stopTask]);

  if (!auth?.requireLogin || error || !usage) return null;
  const temp = usage.temp;
  if (!temp && (!usage.lifetime || !usage.session)) return null;

  const tempRemainingShort = temp ? formatRemainingShort(temp.expires_at) : null;
  const badgeLabel = temp
    ? `${temp.percent_remaining}% left${
        tempRemainingShort != null ? ` · ${tempRemainingShort}` : ''
      }`
    : formatTokenCount(usage.lifetime!.total_tokens);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          variant="secondary"
          className="h-7 cursor-pointer gap-1 px-2 font-mono tabular-nums"
          title={temp ? 'Demo allowance' : 'Token usage'}
        >
          <Coins className="h-3.5 w-3.5" />
          {badgeLabel}
        </Badge>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 space-y-3 text-sm">
        {temp ? (
          <>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Demo allowance
            </div>
            {(() => {
              const remaining = formatRemaining(temp.expires_at);
              if (remaining == null) return null;
              return (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Time left
                  </span>
                  <span className="font-mono tabular-nums font-semibold">{remaining}</span>
                </div>
              );
            })()}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Allowance
                </span>
                <span className="font-mono tabular-nums font-semibold">
                  {temp.percent_remaining}% left
                </span>
              </div>
              <Progress value={100 - temp.percent_remaining} className="h-2" />
            </div>
            {temp.expires_at ? (
              <div className="text-xs text-muted-foreground">
                Expires {formatTimestamp(temp.expires_at)}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Token usage
            </div>
            <UsageRow
              label="This session"
              input={usage.session!.input_tokens}
              output={usage.session!.output_tokens}
              total={usage.session!.total_tokens}
            />
            <UsageRow
              label="All time"
              input={usage.lifetime!.input_tokens}
              output={usage.lifetime!.output_tokens}
              total={usage.lifetime!.total_tokens}
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
